import * as React from 'react';
import { connect } from 'react-redux';

import InlineEditingWrapper from 'components/inline-editing-wrapper/InlineEditingWrapper';
import Input from 'components/inputs/input/Input';
import Spinner from 'components/spinner/Spinner';
import Note from '../note/Note';

import { addNote, deleteNote, updateNote } from 'modules/notes/reducers/notes';
import { fetchInfluencersMeta } from 'modules/influencers/reducers/influencersMeta';

import PromptExit from 'libs/router/prompt-exit/PromptExit';
import { isRequired, maxLength } from 'utils/validators';
import { getPlainText, isEmptyEditorText } from 'utils/editor';
import { __t, applyTranslations } from 'utils/language';

import type { ContextMenuItem, Note as NoteAlias, Notes } from 'types';

import './notes-list.scss';

applyTranslations(require('./notes-list.i18n.json'));

const NOTE_ID_NOT_SELECTED = -1;
const NODE_ID_NEW = -99;
const NOTE_MAX_LENGTH = 1000;

type StateProps = {
  notes: Notes,
  currentAccountId: number | undefined | null
};

type OwnProps = {
  influencerProfileId: number,
  campaignId: number,
  context: string
};

type DispatchProps = {
  dispatchAddNote: (c: string, b: number, a?: number | null) => Promise<any>,
  dispatchDeleteNote: (c: number, b: NoteAlias, a?: number | null) => Promise<any>,
  dispatchUpdateNote: (d: string, c: number, b: NoteAlias, a?: number | null) => Promise<any>,
  dispatchUpdateInfluencerMeta: (a: number) => Promise<void>
};

type Props = StateProps & OwnProps & DispatchProps;

type State = {
  currentEditingNoteId: number
};

class NotesList extends React.Component<Props, State> {
  state: State = {
    currentEditingNoteId: NOTE_ID_NOT_SELECTED
  };

  getContextMenuForDuplication = (note: NoteAlias): Array<ContextMenuItem> => ([
    {
      type: 'separator'
    },
    {
      name: 'duplicate',
      text: __t('notes_list.context_menu_duplicate'),
      meta: {
        noteId: note.id
      },
      onClick: () => this.handleDuplicateNote(note)
    }
  ]);

  handleAddCampaignNote = (text?: string | null) => {
    const { campaignId } = this.props;
    this.handleAddNote(text, campaignId);
  };

  handleAddNote = (text?: string | null, campaignId?: number | null) => {
    const { dispatchAddNote, influencerProfileId } = this.props;

    const trimmedText = text ? text.replace(/\n$/, '') : '';
    const hasEmptyNewNote = isEmptyEditorText(trimmedText);

    if (hasEmptyNewNote || !trimmedText) {
      this.handleExitEditMode();
      return;
    }

    dispatchAddNote(trimmedText, influencerProfileId, campaignId)
      .then(this.handleExitEditMode)
      .then(this.updateInfluencerMeta);
  };

  handleDeleteNote = (note: NoteAlias): void => {
    const { campaignId, dispatchDeleteNote, influencerProfileId } = this.props;

    // Deleting notes enabled only for campaign in this component
    dispatchDeleteNote(influencerProfileId, note, campaignId)
      .then(this.updateInfluencerMeta);
  };

  handleDuplicateNote = (note: NoteAlias) => {
    this.handleAddNote(note.text);
  };

  handleEditNote = (note: NoteAlias, text?: string | null): void => {
    const { campaignId, influencerProfileId, dispatchUpdateNote } = this.props;
    const trimmedText = text ? text.replace(/\n$/, '') : '';

    // Editing notes enabled only for campaign in this component
    dispatchUpdateNote(trimmedText, influencerProfileId, note, campaignId)
      .then(this.handleExitEditMode);
  };

  handleExitEditMode = (): void => {
    this.setState({
      currentEditingNoteId: NOTE_ID_NOT_SELECTED
    });
  };

  handleModified = (currentEditingNoteId: number): void => {
    this.setState({
      currentEditingNoteId
    });
  };

  handleNewNote = (): void => {
    this.setState({
      currentEditingNoteId: NODE_ID_NEW
    });
  };

  isEditModeAllowed(note: NoteAlias) {
    const { context } = this.props;

    if (context !== 'campaign') {
      return false;
    }

    return this.isEditModeAllowedForNote(note);
  }

  isEditModeAllowedForNote(note: NoteAlias) {
    const { currentAccountId } = this.props;
    const { currentEditingNoteId } = this.state;

    return (currentEditingNoteId === note.id || currentEditingNoteId === NOTE_ID_NOT_SELECTED) &&
      note.createdBy.id === currentAccountId;
  }

  updateInfluencerMeta = () => {
    const { influencerProfileId, dispatchUpdateInfluencerMeta } = this.props;
    dispatchUpdateInfluencerMeta(influencerProfileId);
  };

  renderAddNoteForm(): React.ReactNode {
    return (
      <div className="notes-list__form">
        <InlineEditingWrapper
          inGlobalEditMode={false}
          onSubmit={this.handleAddCampaignNote}
          onCancel={this.handleAddCampaignNote}
          onEnterEditMode={this.handleNewNote}
          renderViewMode={() => this.renderNoteEdit(false)}
          renderEditMode={() => this.renderNoteEdit(true)}
          initialValue=""
          validatorResolvers={[
            (value) => isRequired(value),
            (value) => maxLength(getPlainText(value), NOTE_MAX_LENGTH)
          ]}
        />
      </div>
    );
  }

  renderNoteEdit = (isAutoFocusEnabled: boolean) => (
    <Input
      width="responsive"
      multiline
      autoFocus={isAutoFocusEnabled}
      value="" // will be overwritten in InlineEditingWrapper
      placeholder={__t('notes_list.edit_placeholder')}
    />
  );

  renderPlaceholder() {
    return (
      <div className="notes-list__placeholder">
        {__t('notes_list.placeholder')}
      </div>
    );
  }

  render() {
    const { context, notes } = this.props;
    const { currentEditingNoteId } = this.state;
    const isCampaignContext = context === 'campaign';
    const notesList = isCampaignContext ? notes.campaignList : notes.creatorList;

    return (
      <div className="notes-list">
        {
          currentEditingNoteId !== NOTE_ID_NOT_SELECTED &&
            <PromptExit type="panel" />
        }
        {
          isCampaignContext
            ? this.renderAddNoteForm()
            : this.renderPlaceholder()
        }
        {
          notes.isLoading && (
            <Spinner size="small" position="inline" color="light" />
          )
        }
        {
          !notes.isLoading && notesList.map(note => (
            <Note
              key={note.id}
              note={note}
              hideOrganizationName
              editorClassName="notes-list__form"
              isEditModeAllowed={this.isEditModeAllowed(note)}
              onCancel={this.handleExitEditMode}
              onDelete={this.handleDeleteNote}
              onEdit={this.handleEditNote}
              contextMenuItems={isCampaignContext ? this.getContextMenuForDuplication(note) : []}
              onModified={this.handleModified}
            />
          ))
        }
      </div>
    );
  }
}

const mapStateToProps = ({ account, notes }): StateProps => ({
  currentAccountId: account.currentAccount ? account.currentAccount.id : null,
  notes
});

const mapDispatchToProps = (dispatch): DispatchProps => ({
  dispatchAddNote: (text: string, influencerId: number, campaignId?: number | null) => dispatch(addNote(text, influencerId, campaignId)),
  dispatchDeleteNote: (influencerId: number, note: NoteAlias, campaignId?: number | null) => dispatch(deleteNote(influencerId, note, campaignId)),
  dispatchUpdateNote: (text: string, influencerId: number, note: NoteAlias, campaignId?: number | null) => dispatch(updateNote(text, influencerId, note, campaignId)),
  dispatchUpdateInfluencerMeta: (influencerId) => dispatch(fetchInfluencersMeta([influencerId]))
});

export default connect(mapStateToProps, mapDispatchToProps)(NotesList);
